import React, { useEffect, useState, useContext } from 'react';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    Search, Filter, Plus, Edit2, Trash2, X, Pill, AlertTriangle, CheckCircle, FileText
} from 'lucide-react';

const Drugs = () => {
    const { user } = useContext(AuthContext);
    const [drugs, setDrugs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Состояния фильтров
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');

    // Состояния модалки
    const [showModal, setShowModal] = useState(false);
    const [currentDrug, setCurrentDrug] = useState(null);
    const [dosageInput, setDosageInput] = useState(''); // Для поля ввода тегов

    const [formData, setFormData] = useState({
        name: '', trade_name: '', form: '', category: '', manufacturer: '', 
        description: '', is_prescription: false, is_narcotic: false,
        dosages: [] // Массив строк
    });

    useEffect(() => {
        fetchDrugs();
    }, []);

    const fetchDrugs = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/drugs');
            setDrugs(res.data);
        } catch (error) { console.error("Ошибка загрузки:", error); } 
        finally { setIsLoading(false); }
    };

    // --- ЛОГИКА ТЕГОВ (ДОЗИРОВКИ) ---
    const handleAddDosage = (e) => {
        if (e.key === 'Enter' && dosageInput.trim()) {
            e.preventDefault();
            const newTag = dosageInput.trim();
            if (!formData.dosages.includes(newTag)) {
                setFormData({ ...formData, dosages: [...formData.dosages, newTag] });
            }
            setDosageInput('');
        }
    };

    const handleRemoveDosage = (tagToRemove) => {
        setFormData({ ...formData, dosages: formData.dosages.filter(t => t !== tagToRemove) });
    };

    // --- ЛОГИКА ФОРМЫ ---
    const openModal = (drug = null) => {
        setCurrentDrug(drug);
        if (drug) {
            setFormData({
                name: drug.name, trade_name: drug.trade_name, form: drug.form,
                category: drug.category, manufacturer: drug.manufacturer, 
                description: drug.description, is_prescription: drug.is_prescription, 
                is_narcotic: drug.is_narcotic, 
                // Превращаем строку "500мг, 200мг" в массив для тегов
                dosages: drug.dosages ? drug.dosages.split(',').map(d => d.trim()) : []
            });
        } else {
            setFormData({
                name: '', trade_name: '', form: '', category: '', manufacturer: '', 
                description: '', is_prescription: false, is_narcotic: false, dosages: []
            });
        }
        setShowModal(true);
        setDosageInput('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Превращаем массив тегов обратно в строку перед отправкой на сервер
        const payload = {
            ...formData,
            dosages: formData.dosages.join(', '),
            added_by: user?.email || 'admin'
        };

        try {
            if (currentDrug) {
                const res = await api.put(`/drugs/${currentDrug.id}`, payload);
                setDrugs(drugs.map(d => d.id === currentDrug.id ? res.data : d));
            } else {
                const res = await api.post('/drugs', payload);
                setDrugs([res.data, ...drugs]); // Добавляем в начало
            }
            setShowModal(false);
        } catch (error) { alert("Ошибка сохранения"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить это лекарство?")) return;
        try {
            await api.delete(`/drugs/${id}`);
            setDrugs(drugs.filter(d => d.id !== id));
        } catch (error) { alert("Ошибка удаления"); }
    };

    // --- ФИЛЬТРАЦИЯ И СТАТИСТИКА ---
    const filteredDrugs = drugs.filter(d => {
        const matchSearch = d.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            d.category.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCategory = filterCategory === 'all' || d.category === filterCategory;
        return matchSearch && matchCategory;
    });

    const uniqueCategories = [...new Set(drugs.map(d => d.category).filter(Boolean))];

    return (
        <div className="content-wrapper drug-book">
            <div className="db-container">
                
                {/* ШАПКА */}
                <header className="db-header">
                    <div>
                        <h2>Справочник лекарств</h2>
                        <p className="db-text-sm db-text-gray">Управление базой лекарственных препаратов</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button className="db-btn db-btn-primary" onClick={() => openModal()}>
                            <Plus size={18} /> Добавить лекарство
                        </button>
                    </div>
                </header>

                {/* СТАТИСТИКА */}
                <section className="db-stats">
                    <div className="db-stat-card">
                        <div className="db-stat-icon db-icon-blue"><Pill /></div>
                        <div>
                            <p className="db-text-xs db-text-gray">Всего лекарств</p>
                            <p className="db-stat-val">{drugs.length}</p>
                        </div>
                    </div>
                    <div className="db-stat-card">
                        <div className="db-stat-icon db-icon-green"><CheckCircle /></div>
                        <div>
                            <p className="db-text-xs db-text-gray">Обычные (Без рецепта)</p>
                            <p className="db-stat-val">{drugs.filter(d => !d.is_prescription && !d.is_narcotic).length}</p>
                        </div>
                    </div>
                    <div className="db-stat-card">
                        <div className="db-stat-icon db-icon-orange"><FileText /></div>
                        <div>
                            <p className="db-text-xs db-text-gray">Рецептурные</p>
                            <p className="db-stat-val">{drugs.filter(d => d.is_prescription).length}</p>
                        </div>
                    </div>
                    <div className="db-stat-card">
                        <div className="db-stat-icon db-icon-red"><AlertTriangle /></div>
                        <div>
                            <p className="db-text-xs db-text-gray">Наркотические</p>
                            <p className="db-stat-val">{drugs.filter(d => d.is_narcotic).length}</p>
                        </div>
                    </div>
                </section>

                {/* ПОИСК И ФИЛЬТРЫ */}
                <section className="db-controls">
                    <div className="db-input-group">
                        <Search />
                        <input 
                            type="text" 
                            className="db-input db-input-search" 
                            placeholder="Поиск по названию, категории..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="db-filter">
                        <Filter size={20} color="#9ca3af" />
                        <select className="db-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                            <option value="all">Все категории</option>
                            {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                    </div>
                </section>
                
                <div style={{ textAlign: 'left', fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                    Показано: {filteredDrugs.length} из {drugs.length}
                </div>

                {/* СПИСОК */}
                <main className="db-list">
                    {isLoading ? (
                        <div style={{padding: '2rem', textAlign: 'center', color: '#6b7280'}}>Загрузка...</div>
                    ) : filteredDrugs.length === 0 ? (
                        <div style={{padding: '2rem', textAlign: 'center', color: '#6b7280'}}>Ничего не найдено</div>
                    ) : (
                        filteredDrugs.map(drug => (
                            <div key={drug.id} className="db-list-item">
                                <div>
                                    <div className="db-item-header">
                                        <h3>{drug.name}</h3>
                                        <span className="db-item-dosage">{drug.dosages}</span>
                                        {drug.is_prescription && <span className="db-badge db-badge-orange">Рецептурное</span>}
                                        {drug.is_narcotic && <span className="db-badge db-badge-red">⚠️ Наркотическое</span>}
                                    </div>
                                    <div className="db-item-details">
                                        <p>Форма: {drug.form}</p>
                                        {drug.trade_name && <p>Торговое название: {drug.trade_name}</p>}
                                        <p>Категория: {drug.category}</p>
                                        <p className="db-item-desc">{drug.description}</p>
                                        {drug.added_by && <p className="db-item-author">Добавил: {drug.added_by}</p>}
                                    </div>
                                </div>
                                <div className="db-item-actions">
                                    <button className="db-btn db-btn-outline db-btn-icon" onClick={() => openModal(drug)} title="Редактировать">
                                        <Edit2 size={16} />
                                    </button>
                                    <button className="db-btn db-btn-outline db-btn-icon db-btn-danger" onClick={() => handleDelete(drug.id)} title="Удалить">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </main>

            </div>

            {/* МОДАЛЬНОЕ ОКНО ДОБАВЛЕНИЯ/РЕДАКТИРОВАНИЯ */}
            {showModal && (
                <div className="db-modal active" onClick={() => setShowModal(false)}>
                    <div className="db-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="db-modal-header">
                            <h3>{currentDrug ? 'Редактировать лекарство' : 'Добавить лекарство'}</h3>
                            <button className="db-modal-close" onClick={() => setShowModal(false)}><X size={24} /></button>
                        </div>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="db-modal-body">
                                <div className="db-form-group">
                                    <label className="db-label">Название <span>*</span></label>
                                    <input type="text" className="db-input" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} placeholder="Парацетамол" required />
                                </div>
                                <div className="db-form-group">
                                    <label className="db-label">Торговое название</label>
                                    <input type="text" className="db-input" value={formData.trade_name} onChange={e=>setFormData({...formData, trade_name: e.target.value})} placeholder="Панадол, Эффералган" />
                                </div>
                                <div className="db-form-row db-form-group">
                                    <div>
                                        <label className="db-label">Форма выпуска <span>*</span></label>
                                        <input type="text" className="db-input" value={formData.form} onChange={e=>setFormData({...formData, form: e.target.value})} placeholder="таблетки, сироп..." required />
                                    </div>
                                    <div>
                                        <label className="db-label">Дозировка (нажмите Enter)</label>
                                        <div className="db-tags-input">
                                            {formData.dosages.map(tag => (
                                                <span key={tag} className="db-tag">
                                                    {tag} <button type="button" onClick={() => handleRemoveDosage(tag)}><X size={12}/></button>
                                                </span>
                                            ))}
                                            <input 
                                                type="text" 
                                                value={dosageInput}
                                                onChange={e => setDosageInput(e.target.value)}
                                                onKeyDown={handleAddDosage}
                                                placeholder={formData.dosages.length === 0 ? "Например: 500мг" : ""}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="db-form-group">
                                    <label className="db-label">Категория <span>*</span></label>
                                    <input type="text" className="db-input" value={formData.category} onChange={e=>setFormData({...formData, category: e.target.value})} placeholder="Жаропонижающее..." required />
                                </div>
                                <div className="db-form-group">
                                    <label className="db-label">Производитель</label>
                                    <input type="text" className="db-input" value={formData.manufacturer} onChange={e=>setFormData({...formData, manufacturer: e.target.value})} placeholder="Название компании" />
                                </div>
                                <div className="db-form-group">
                                    <label className="db-label">Описание</label>
                                    <textarea className="db-input" value={formData.description} onChange={e=>setFormData({...formData, description: e.target.value})} placeholder="Краткое описание..."></textarea>
                                </div>
                                <div className="db-form-group">
                                    <label className="db-checkbox-group">
                                        <input type="checkbox" checked={formData.is_prescription} onChange={e=>setFormData({...formData, is_prescription: e.target.checked})} />
                                        <span className="db-text-sm">Рецептурный препарат</span>
                                    </label>
                                    <label className="db-checkbox-group">
                                        <input type="checkbox" checked={formData.is_narcotic} onChange={e=>setFormData({...formData, is_narcotic: e.target.checked})} />
                                        <span className="db-text-sm" style={{color: '#dc2626'}}>⚠️ Наркотический / психотропный препарат</span>
                                    </label>
                                </div>
                            </div>
                            <div className="db-modal-footer">
                                <button type="button" className="db-btn db-btn-outline" onClick={() => setShowModal(false)}>Отмена</button>
                                <button type="submit" className="db-btn db-btn-primary">Сохранить</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Drugs;